import { vec3 } from "gl-matrix";
import { URLSearchParamsInit } from "react-router-dom";

import { OsrsMenuEntry } from "../components/rs/menu/OsrsMenu";
import { MenuTargetType } from "../rs/MenuEntry";
import { CacheSystem } from "../rs/cache/CacheSystem";
import { CacheLoaderFactory, getCacheLoaderFactory } from "../rs/cache/loader/CacheLoaderFactory";
import { BasTypeLoader } from "../rs/config/bastype/BasTypeLoader";
import { LocTypeLoader } from "../rs/config/loctype/LocTypeLoader";
import { NpcTypeLoader } from "../rs/config/npctype/NpcTypeLoader";
import { ObjTypeLoader } from "../rs/config/objtype/ObjTypeLoader";
import { SeqTypeLoader } from "../rs/config/seqtype/SeqTypeLoader";
import { VarManager } from "../rs/config/vartype/VarManager";
import { MapFileIndex, getMapSquareId } from "../rs/map/MapFileIndex";
import { SeqFrameLoader } from "../rs/model/seq/SeqFrameLoader";
import { Pathfinder } from "../rs/pathfinder/Pathfinder";
import { TextureLoader } from "../rs/texture/TextureLoader";
import { isTouchDevice, isWallpaperEngine } from "../util/DeviceUtil";
import { CacheList, LoadedCache } from "./Caches";
import { Camera, CameraView, ProjectionType } from "./Camera";
import { InputManager } from "./InputManager";
import { MapManager } from "./MapManager";
import { MapViewerRenderer } from "./MapViewerRenderer";
import { MapViewerRendererType, createRenderer } from "./MapViewerRenderers";
import { NpcSpawn } from "./data/npc/NpcSpawn";
import { ObjSpawn } from "./data/obj/ObjSpawn";
import { RenderDataWorkerPool } from "./worker/RenderDataWorkerPool";

export type ExportResolution = 64 | 128;
export type ExportFormat = "png" | "jpeg";

const DEFAULT_RENDER_DISTANCE = isWallpaperEngine ? 512 : 128;
const DEFAULT_ORTHO_ZOOM = 70;

const CACHED_MAP_IMAGE_PREFIX = "/map-images/";

export class MapViewer {
    inputManager: InputManager = new InputManager();
    camera: Camera;

    pathfinder: Pathfinder = new Pathfinder();

    renderer: MapViewerRenderer;

    // Cache
    loadedCache!: LoadedCache;
    cacheSystem!: CacheSystem;
    loaderFactory!: CacheLoaderFactory;

    textureLoader!: TextureLoader;
    seqTypeLoader!: SeqTypeLoader;
    seqFrameLoader!: SeqFrameLoader;

    locTypeLoader!: LocTypeLoader;
    objTypeLoader!: ObjTypeLoader;
    npcTypeLoader!: NpcTypeLoader;

    basTypeLoader!: BasTypeLoader;

    varManager!: VarManager;

    mapFileIndex!: MapFileIndex;

    isNewTextureAnim: boolean = false;

    // Settings

    // Tile distance
    renderDistance: number = DEFAULT_RENDER_DISTANCE;
    // Map square distance
    unloadDistance: number = 2;
    // Map square distance
    lodDistance: number = 3;

    tooltips: boolean = !isTouchDevice;
    debugId: boolean = false;

    // State
    needsSearchParamUpdate: boolean = false;
    lastTimeSearchParamsUpdated: number = 0;

    menuOpen: boolean = false;
    menuOpenedFrame: number = 0;
    menuX: number = -1;
    menuY: number = -1;
    menuEntries: OsrsMenuEntry[] = [];

    debugText?: string;

    mapImageUrls: Map<number, string> = new Map();
    minimapImageUrls: Map<number, string> = new Map();
    loadingMapImageIds: Set<number> = new Set();

    cameraSpeed: number = 1;

    constructor(
        readonly workerPool: RenderDataWorkerPool,
        readonly cacheList: CacheList,
        readonly objSpawns: ObjSpawn[],
        public npcSpawns: NpcSpawn[],
        readonly mapImageCache: Cache,
        rendererType: MapViewerRendererType,
        cache: LoadedCache,
    ) {
        this.renderer = createRenderer(rendererType, this);
        this.camera = this.createCamera();
        this.initializeBattlemapCreatorSettings();
        this.initCache(cache);
    }

    getSearchParams(): URLSearchParamsInit {
        const cx = this.camera.getPosX().toFixed(2).toString();
        const cz = this.camera.getPosZ().toFixed(2).toString();

        const yaw = this.camera.yaw & 2047;
        const y = yaw.toString();

        const params: any = {
            cx,
            cz,
            y,
            z: this.camera.orthoZoom.toString(),
        };

        if (this.loadedCache.info.name !== this.cacheList.latest.name) {
            params["cache"] = this.loadedCache.info.name;
        }

        params["v"] = 1;

        return params;
    }

    applySearchParams(searchParams: URLSearchParams) {
        const cx = searchParams.get("cx");
        const cy = searchParams.get("cy");
        const cz = searchParams.get("cz");

        const yaw = searchParams.get("y");

        const zoom = searchParams.get("z");
        if (zoom) {
            this.camera.orthoZoom = parseInt(zoom);
        }

        if (cx && cz) {
            // Use cy from URL if present (backwards compat), otherwise keep default Y
            const posY = cy ? -parseFloat(cy) : this.camera.pos[1];
            const pos = vec3.fromValues(parseFloat(cx), posY, parseFloat(cz));
            this.camera.pos = pos;
        }
        if (yaw) {
            this.camera.yaw = parseInt(yaw);
        }
    }

    init(): void {
        this.workerPool.loadCachedMapImages().then((mapImageUrls) => {
            mapImageUrls.forEach((value, key) => this.mapImageUrls.set(key, value));
        });
    }

    initCache(cache: LoadedCache): void {
        this.loadedCache = cache;
        this.cacheSystem = CacheSystem.fromFiles(cache.type, cache.files);
        this.loaderFactory = getCacheLoaderFactory(cache.info, this.cacheSystem);
        this.workerPool.initCache(cache, this.objSpawns, this.npcSpawns);
        this.clearMapImageUrls();

        this.textureLoader = this.loaderFactory.getTextureLoader();
        this.seqTypeLoader = this.loaderFactory.getSeqTypeLoader();
        this.seqFrameLoader = this.loaderFactory.getSeqFrameLoader();
        this.locTypeLoader = this.loaderFactory.getLocTypeLoader();
        this.objTypeLoader = this.loaderFactory.getObjTypeLoader();
        this.npcTypeLoader = this.loaderFactory.getNpcTypeLoader();
        this.basTypeLoader = this.loaderFactory.getBasTypeLoader();

        this.varManager = new VarManager(this.loaderFactory.getVarBitTypeLoader());
        const questTypeLoader = this.loaderFactory.getQuestTypeLoader();
        if (questTypeLoader) {
            this.varManager.setQuestsCompleted(questTypeLoader);
        }

        const mapFileLoader = this.loaderFactory.getMapFileLoader();
        this.mapFileIndex = mapFileLoader.mapFileIndex;

        this.isNewTextureAnim = cache.info.game === "runescape" && cache.info.revision >= 681;

        this.renderer.initCache();

        this.updateSearchParams();
    }

    createCamera(): Camera {
        // Default grid settings (match GridRenderer2D defaults)
        let worldX = 3200;
        let worldZ = 3200;
        let widthInCells = 30;
        let heightInCells = 30;

        try {
            const stored = localStorage.getItem("osrs-battlemap-grid-settings");
            if (stored) {
                const parsed = JSON.parse(stored);
                if (typeof parsed.worldX === "number") worldX = parsed.worldX;
                if (typeof parsed.worldZ === "number") worldZ = parsed.worldZ;
                if (typeof parsed.widthInCells === "number") widthInCells = parsed.widthInCells;
                if (typeof parsed.heightInCells === "number") heightInCells = parsed.heightInCells;
            }
        } catch {
            // Ignore errors (localStorage unavailable, invalid JSON, etc.)
        }

        // Center camera on grid center (worldX/worldZ is top-left corner,
        // grid extends right (+X) and down (-Z))
        const cameraX = worldX + widthInCells / 2;
        const cameraZ = worldZ - heightInCells / 2;

        const camera = new Camera(cameraX, -26, cameraZ, -512, 0);
        camera.setProjectionType(ProjectionType.ORTHO);
        camera.orthoZoom = DEFAULT_ORTHO_ZOOM;
        return camera;
    }

    initializeBattlemapCreatorSettings(): void {
        this.tooltips = false;
    }

    setRenderer(renderer: MapViewerRenderer): void {
        this.renderer = renderer;
        this.renderer.initCache();
        this.resetMenu();
    }

    /**
     * Sets the camera position to a new arbitrary position
     * @param newView Any of the items you want to move: Position, pitch, yaw
     */
    setCamera(newView: Partial<CameraView>): void {
        if (newView.position) {
            vec3.copy(this.camera.pos, newView.position);
        }
        if (newView.pitch !== undefined) {
            this.camera.pitch = newView.pitch;
        }
        if (newView.yaw !== undefined) {
            this.camera.yaw = newView.yaw;
        }
        if (newView.fov !== undefined) {
            this.camera.fov = newView.fov;
        }
        if (newView.orthoZoom !== undefined) {
            this.camera.orthoZoom = newView.orthoZoom;
        }
        this.camera.updated = true;
    }

    updateSearchParams(): void {
        this.needsSearchParamUpdate = true;
        this.lastTimeSearchParamsUpdated = performance.now();
    }

    closeMenu = () => {
        this.menuOpen = false;
        this.menuX = -1;
        this.menuY = -1;
        this.renderer.canvas.focus();
    };

    resetMenu = () => {
        this.closeMenu();
        this.menuOpenedFrame = 0;
    };

    // TODO: remove
    onExamine = (entry: OsrsMenuEntry) => {
        let lookupType: string | undefined;
        switch (entry.targetType) {
            case MenuTargetType.NPC:
                lookupType = "npc";
                break;
            case MenuTargetType.LOC:
                lookupType = "object";
                break;
            case MenuTargetType.OBJ:
                lookupType = "item";
                break;
        }
        if (lookupType) {
            window.open(
                `https://oldschool.runescape.wiki/w/Special:Lookup?type=${lookupType}&id=${entry.targetId}`,
                "_blank",
            );
        }
        this.closeMenu();
    };

    updateVars(): void {
        this.workerPool.setVars(this.varManager.values);
    }

    static getCachedMapImageUrl(mapX: number, mapY: number): string {
        return CACHED_MAP_IMAGE_PREFIX + `${mapX}_${mapY}.png`;
    }

    async queueLoadMapImage(mapX: number, mapY: number) {
        const mapManager = this.renderer.mapManager;
        const mapId = getMapSquareId(mapX, mapY);
        if (
            this.loadingMapImageIds.size > this.workerPool.size * 4 ||
            this.mapImageUrls.has(mapId) ||
            this.loadingMapImageIds.has(mapId) ||
            mapManager.invalidMapIds.has(mapId) ||
            mapManager.loadingMapIds.has(mapId)
        ) {
            return;
        }
        this.loadingMapImageIds.add(mapId);

        const minimapData = await this.workerPool.queueMapImage(mapX, mapY, 0, true);
        if (minimapData) {
            const url = URL.createObjectURL(minimapData.minimapBlob);
            this.setMapImageUrl(mapX, mapY, url, false);
        } else {
            mapManager.invalidMapIds.add(mapId);
        }

        this.loadingMapImageIds.delete(mapId);
    }

    getMapImageUrl(mapX: number, mapY: number, minimap: boolean): string | undefined {
        if (mapX < 0 || mapY < 0 || mapX >= MapManager.MAX_MAP_X || mapY >= MapManager.MAX_MAP_Y) {
            return undefined;
        }
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        if (minimap) {
            this.renderer.mapManager.loadMap(mapX, mapY);
        } else {
            this.queueLoadMapImage(mapX, mapY);
        }
        const mapId = getMapSquareId(mapX, mapY);
        return urls.get(mapId);
    }

    setMapImageUrl(
        mapX: number,
        mapY: number,
        url: string,
        minimap: boolean,
        cache: boolean = true,
    ): void {
        const mapId = getMapSquareId(mapX, mapY);
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        const old = urls.get(mapId);
        if (old) {
            URL.revokeObjectURL(old);
        }
        if (cache) {
            fetch(url).then((resp) => {
                if (resp.ok) {
                    const request = new Request(MapViewer.getCachedMapImageUrl(mapX, mapY), {
                        headers: {
                            "RS-Cache-Name": this.loadedCache.info.name,
                        },
                    });
                    this.mapImageCache.put(request, resp);
                }
            });
        }
        urls.set(mapId, url);
    }

    clearMapImageUrls(): void {
        for (const url of this.mapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        for (const url of this.minimapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        this.mapImageUrls.clear();
        this.minimapImageUrls.clear();
        this.loadingMapImageIds.clear();
    }

    async exportBattlemap(
        resolution: ExportResolution = 128,
        format: ExportFormat = "png",
    ): Promise<Blob | null> {
        const gridRenderer = this.renderer.gridRenderer;
        const settings = gridRenderer.getSettings();
        const gridWidthInCells = settings.widthInCells;
        const gridHeightInCells = settings.heightInCells;

        // Calculate target dimensions based on resolution
        const targetWidth = gridWidthInCells * resolution;
        const targetHeight = gridHeightInCells * resolution;

        // Calculate orthoZoom for the target resolution
        // Formula: cellSizePx = orthoZoom / 2, so orthoZoom = resolution * 2
        const targetOrthoZoom = resolution * 2;

        // Save current camera state
        const originalCamPos = vec3.clone(this.camera.floatPosition);
        const originalOrthoZoom = this.camera.orthoZoom;
        const originalSnapToGrid = this.camera.snapToGrid;

        // Disable snap-to-grid during export so fractional center positions are exact.
        // With odd grid dimensions, the center is at X.5 — snapping would floor it,
        // shifting the view by half a cell and causing half-squares on the edges.
        this.camera.snapToGrid = false;

        // Move camera to grid center for export
        // worldX/worldZ is top-left corner, grid extends right (+X) and down (-Z)
        const gridCenterX = settings.worldX + gridWidthInCells / 2;
        const gridCenterZ = settings.worldZ - gridHeightInCells / 2;
        this.camera.teleport(gridCenterX, undefined, gridCenterZ);

        // Render the scene at the target resolution and get the bitmap
        const mapBitmap = await this.renderer.renderForExport(
            targetWidth,
            targetHeight,
            targetOrthoZoom,
        );

        // Restore camera state
        this.camera.snapToGrid = originalSnapToGrid;
        this.camera.teleport(originalCamPos[0], originalCamPos[1], originalCamPos[2]);
        this.camera.orthoZoom = originalOrthoZoom;
        this.camera.updated = true;

        // Create the final export canvas
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = targetWidth;
        exportCanvas.height = targetHeight;
        const ctx = exportCanvas.getContext("2d");
        if (!ctx) return null;

        // Draw the rendered scene to the export canvas
        ctx.drawImage(mapBitmap, 0, 0, targetWidth, targetHeight);

        // Create a temporary canvas for the grid overlay
        const gridCanvas = document.createElement("canvas");
        gridCanvas.width = targetWidth;
        gridCanvas.height = targetHeight;

        // Calculate scale factor for line widths (base is 64px per cell)
        const scaleFactor = resolution / 64;

        // Create a temporary camera state for grid rendering at export position
        const exportCamera = this.createCamera();
        exportCamera.snapToGrid = false;
        exportCamera.teleport(gridCenterX, undefined, gridCenterZ);
        exportCamera.orthoZoom = targetOrthoZoom;
        exportCamera.update(targetWidth, targetHeight);

        // Render the grid at the export resolution
        gridRenderer.renderToCanvas(gridCanvas, exportCamera, scaleFactor);

        // Composite the grid overlay onto the export canvas
        ctx.drawImage(gridCanvas, 0, 0);

        // Draw watermark in the bottom-right corner
        const watermarkText = "Made with osrstabletop.com";
        const baseFontSize = 24;
        const fontSize = Math.round(baseFontSize * scaleFactor);
        const padding = Math.round(12 * scaleFactor);
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = "right";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
        ctx.fillText(watermarkText, targetWidth - padding, targetHeight - padding);

        const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
        const quality = format === "jpeg" ? 0.92 : undefined;
        return await new Promise((resolve) => exportCanvas.toBlob(resolve, mimeType, quality));
    }
}
